/**
 * Client-side Gemini service — thin wrappers around Firebase callable functions.
 *
 * All Gemini API calls now run server-side (Cloud Functions) so the key never
 * reaches the browser bundle. Function signatures are unchanged so all existing
 * callers in Itinerary.tsx / ImportBookingModal.tsx work without modification.
 */

import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";
import type { ParsedBooking } from "../types";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Recommendation {
  name: string;
  desc: string;
  address: string;
  type: "attraction" | "food" | "nightlife" | "shopping";
  category: string;
  rating?: number;
}

// ── Callable references ───────────────────────────────────────────────────────

const _getDestinationInfoCf = httpsCallable<
  { locationName: string },
  { blurb: string; country: string; emoji: string }
>(functions, "getDestinationInfo", { timeout: 35_000 });

const _getRecommendationsCf = httpsCallable<
  { city: string; type: string; preferences?: string },
  Recommendation[]
>(functions, "getRecommendations", { timeout: 65_000 });

const _trackFlightCf = httpsCallable<
  { flightNum: string; date?: string },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Record<string, any> | null
>(functions, "trackFlight", { timeout: 65_000 });

const _parseBookingConfirmationCf = httpsCallable<
  { input: { type: string; content?: string; base64?: string; mimeType?: string } },
  ParsedBooking | null
>(functions, "parseBookingConfirmation", { timeout: 125_000 });

// ── Public API (signatures unchanged from the original client-side version) ──

export async function getDestinationInfo(
  locationName: string
): Promise<{ blurb: string; country: string; emoji: string }> {
  try {
    const result = await _getDestinationInfoCf({ locationName });
    return result.data;
  } catch (error) {
    console.error("getDestinationInfo (CF) failed:", error);
    return {
      blurb: `Exploring the soul of ${locationName}.`,
      country: locationName.split(",").pop()?.trim() || "",
      emoji: "📍",
    };
  }
}

export async function getRecommendations(
  city: string,
  type: "attractions" | "food" | "nightlife" | "shopping",
  preferences = ""
): Promise<Recommendation[]> {
  // Let errors propagate — the caller (ExploreView) handles them and shows
  // a proper message. Swallowing here caused silent blank panels.
  const result = await _getRecommendationsCf({ city, type, preferences });
  return result.data ?? [];
}

export async function trackFlight(
  flightNum: string,
  date?: string
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<Record<string, any> | null> {
  try {
    const result = await _trackFlightCf({ flightNum, date });
    return result.data ?? null;
  } catch (error) {
    console.error("trackFlight (CF) failed:", error);
    return null;
  }
}

export async function parseBookingConfirmation(
  input: { type: "text"; content: string } | { type: "file"; base64: string; mimeType: string }
): Promise<ParsedBooking | null> {
  try {
    const result = await _parseBookingConfirmationCf({ input });
    return result.data ?? null;
  } catch (error) {
    console.error("parseBookingConfirmation (CF) failed:", error);
    return null;
  }
}
